import { useEffect } from 'react';

interface Project {
  id: number;
  title: string;
  category: string;
  description: string;
  fullDescription: string;
  image: string;
  details: {
    client: string;
    duration: string;
    services: string[];
    challenge: string;
    solution: string;
  };
  gallery: string[];
}

interface ProjectModalProps {
  project: Project | null;
  isOpen: boolean;
  onClose: () => void;
}

export default function ProjectModal({ project, isOpen, onClose }: ProjectModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !project) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm cursor-pointer"
        onClick={onClose}
      ></div>
      
      <div className="relative bg-white rounded-2xl max-w-5xl w-full mx-4 max-h-[90vh] overflow-y-auto shadow-2xl">
        <button
          onClick={onClose}
          className="sticky top-4 right-4 float-right z-10 w-10 h-10 bg-gray-900 text-white rounded-full flex items-center justify-center hover:bg-gray-800 transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-xl"></i>
        </button>

        <div className="relative h-96 rounded-t-2xl overflow-hidden">
          <img
            src={project.image}
            alt={project.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
          <div className="absolute bottom-8 left-8 right-8">
            <span className="inline-block px-3 py-1 bg-teal-500 text-white text-xs font-medium rounded-full mb-3">
              {project.category}
            </span>
            <h2 className="text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              {project.title}
            </h2>
            <p className="text-gray-200 text-base">{project.description}</p>
          </div>
        </div>

        <div className="p-8 lg:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
            <div className="bg-gray-50 p-6 rounded-xl">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-user-line text-teal-600 text-xl"></i>
              </div>
              <h4 className="text-sm font-medium text-gray-500 mb-1">Client</h4>
              <p className="text-base font-semibold text-gray-900">{project.details.client}</p>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-time-line text-teal-600 text-xl"></i>
              </div>
              <h4 className="text-sm font-medium text-gray-500 mb-1">Duration</h4>
              <p className="text-base font-semibold text-gray-900">{project.details.duration}</p>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-service-line text-teal-600 text-xl"></i>
              </div>
              <h4 className="text-sm font-medium text-gray-500 mb-1">Services</h4>
              <p className="text-base font-semibold text-gray-900">{project.details.services.join(', ')}</p>
            </div>
          </div>

          <div className="mb-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Project Overview
            </h3>
            <p className="text-gray-600 text-base leading-relaxed">
              {project.fullDescription}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                The Challenge
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                {project.details.challenge}
              </p>
            </div>

            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Our Solution
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                {project.details.solution}
              </p>
            </div>
          </div>

          <div className="mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Project Gallery
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {project.gallery.map((image, index) => (
                <div key={index} className="relative h-64 rounded-xl overflow-hidden group">
                  <img
                    src={image}
                    alt={`${project.title} gallery ${index + 1}`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 pt-8 border-t border-gray-200">
            <a
              href="#booking"
              onClick={onClose}
              className="flex-1 bg-teal-600 text-white py-4 px-6 rounded-lg font-medium text-center hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              Start Your Project
            </a>
            <button
              onClick={onClose}
              className="flex-1 bg-gray-100 text-gray-900 py-4 px-6 rounded-lg font-medium hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
